/**
 * CrystalBall — Rest-of-Season Predictor (v2 redesign)
 *
 * Projected standings table with a dashed red PLAYOFF LINE divider
 * (below-the-line teams fade), projection StatStrip in the header,
 * bull/bear cases, per-player ROS outlook, and JAI's key insight strip.
 */

import { useState, useEffect, Fragment } from 'react'
import { useParams, Link } from 'react-router-dom'
import { leaguesApi } from '../services/api'
import AppShell from '../components/AppShell'
import ToolNav from '../components/ToolNav'
import { JAI_FONTS } from '../components/JaiBrand'
import { V2, V2Page, V2Card, StatStrip, V2SectionHead, BlueTag, JaiSuggestionStrip, JaiSummaryCard, PosChip } from '../components/JaiV2'
import PlayerPhoto from '../components/PlayerPhoto'
import { JAIMascot } from '../components/JAIMascot'

// ── Preview flag ───────────────────────────────────────────────────────────────
const IS_PREVIEW =
  import.meta.env.DEV &&
  new URLSearchParams(window.location.search).get('preview') === '1'

// ── Mock data ──────────────────────────────────────────────────────────────────
const MY_TEAM = 'Rim Reapers'
const PLAYOFF_SPOTS = 4

const PREVIEW_PROJECTION = {
  projectedFinish: 1,
  playoffProb: 94,
  champProb: 38,
  wins: { current: 10, projected: 14 },
  losses: { current: 4, projected: 4 },
  jaiSummary: "Rim Reapers are the clear frontrunner. The schedule is manageable, the roster is healthy, and the competition is in disarray. The biggest threats are a Zion injury (which JAI rates as likely) and a hot streak from Hoop Schemers. If the roster stays intact, this is a championship-calibre finish.",
  bullCase: "Zion stays healthy, Tatum closes the season in MVP form, and you run the table — finishing 14-4 and entering playoffs as the top seed. Championship probability shoots to 58%.",
  bearCase: "Zion misses 3+ weeks, you drop 3 of the next 4, and Hoop Schemers leapfrog you. Still make playoffs at #2 seed, but the path gets harder.",
  keyInsight: "Your hardest remaining stretch is Weeks 16–17 (Schemers, then Dunks & Dimes). Win one of two and the top seed holds in 84% of simulations.",
}

const PREVIEW_STANDINGS = [
  { rank: 1, name: MY_TEAM,                projW: 14, projL: 4,  playoffProb: 94, isMe: true  },
  { rank: 2, name: 'Hoop Schemers',        projW: 12, projL: 6,  playoffProb: 88, isMe: false },
  { rank: 3, name: 'Dunks & Dimes',        projW: 10, projL: 8,  playoffProb: 72, isMe: false },
  { rank: 4, name: 'Fast Break FC',        projW: 10, projL: 8,  playoffProb: 68, isMe: false },
  { rank: 5, name: 'Dunk Dynasty',         projW: 8,  projL: 10, playoffProb: 31, isMe: false },
  { rank: 6, name: 'Playoff or Bust',      projW: 8,  projL: 10, playoffProb: 28, isMe: false },
  { rank: 7, name: 'The Benchmarks',       projW: 6,  projL: 12, playoffProb: 12, isMe: false },
  { rank: 8, name: 'Bench Warmers United', projW: 4,  projL: 14, playoffProb: 2,  isMe: false },
]

const PREVIEW_PLAYERS = [
  { name: 'Jayson Tatum',     pos: 'SF', rosOutlook: 'Elite', rosScore: 92, projection: '34.2 ppg', note: 'On pace for best fantasy season of his career. Elite ROS.' },
  { name: 'Nikola Jokić',     pos: 'C',  rosOutlook: 'Elite', rosScore: 90, projection: '38.1 ppg', note: 'Consistent as ever. Monitor back in weeks 16-17.' },
  { name: 'SGA',              pos: 'PG', rosOutlook: 'Great', rosScore: 81, projection: '31.4 ppg', note: 'OKC leaning on him heavily in playoff push.' },
  { name: 'T. Haliburton',    pos: 'PG', rosOutlook: 'Good',  rosScore: 74, projection: '27.8 ppg', note: 'Great schedule in weeks 16-17. Buy window.' },
  { name: 'Bam Adebayo',      pos: 'C',  rosOutlook: 'Good',  rosScore: 68, projection: '24.2 ppg', note: 'Reliable floor but ceiling is capped. Good depth piece.' },
  { name: 'Zion Williamson',  pos: 'PF', rosOutlook: 'Risk',  rosScore: 51, projection: '26.0 ppg*', note: '* If healthy. JAI projects 40% chance of missing 2+ games. Biggest risk on roster.' },
  { name: 'Marcus Smart',     pos: 'PG', rosOutlook: 'Sell',  rosScore: 38, projection: '18.4 ppg', note: 'Declining usage, injury risk, and tough schedule. Trade if you can.' },
]

const OUTLOOK_COLORS = {
  Elite: V2.blue,
  Great: V2.success,
  Good:  V2.success,
  Risk:  V2.warning,
  Sell:  V2.error,
}

function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return n + (s[(v - 20) % 10] || s[v] || s[0])
}

// ── Main ───────────────────────────────────────────────────────────────────────

export default function CrystalBall() {
  const { id } = useParams()
  const [league, setLeague] = useState(IS_PREVIEW ? { name: 'The Hardwood Cartel' } : null)
  const [loading, setLoading] = useState(!IS_PREVIEW)
  const proj = PREVIEW_PROJECTION

  useEffect(() => {
    if (IS_PREVIEW) return
    leaguesApi.get(id).then(r => {
      setLeague(r.data.league)
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [id])

  if (loading) {
    return (
      <AppShell>
        <div className="flex flex-col items-center justify-center h-full min-h-screen gap-3">
          <JAIMascot size={56} state="thinking" animate={true} />
          <span className="text-sm" style={{ color: V2.faint }}>Loading…</span>
        </div>
      </AppShell>
    )
  }

  const grid = '40px 1fr 90px 1fr 70px'

  return (
    <AppShell>
      <V2Page
        topBar={<ToolNav leagueId={id} />}
        kicker={
          <Link to={`/league/${id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
            {league?.name ? `${league.name} · Rest-of-season projection` : 'Rest-of-season projection'}
          </Link>
        }
        title="Crystal Ball"
        sub="Final standings if the season plays out the way JAI's model expects."
        maxWidth={880}
        action={
          <StatStrip cells={[
            [ordinal(proj.projectedFinish), 'Proj. seed', true],
            [`${proj.wins.projected}-${proj.losses.projected}`, 'Proj. record'],
            [`${proj.playoffProb}%`, 'Playoff odds'],
          ]} />
        }
      >

        {/* ── Projected standings table with playoff line ──────────────── */}
        <V2Card pad="0" style={{ marginBottom: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 12, padding: '11px 20px', borderBottom: '1px solid rgba(10,31,68,0.07)', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.12em', color: V2.faint, fontFamily: JAI_FONTS.mono, textTransform: 'uppercase' }}>
            <span>Seed</span><span>Projected finish</span><span style={{ textAlign: 'right' }}>Record</span><span>Playoff odds</span><span style={{ textAlign: 'right' }}>Odds</span>
          </div>
          {PREVIEW_STANDINGS.map((team, i) => (
            <Fragment key={team.name}>
              {team.rank === PLAYOFF_SPOTS + 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 20px' }}>
                  <div style={{ flex: 1, borderTop: '2px dashed rgba(214,58,58,0.4)' }} />
                  <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.14em', color: V2.error, fontFamily: JAI_FONTS.mono }}>PLAYOFF LINE</span>
                  <div style={{ flex: 1, borderTop: '2px dashed rgba(214,58,58,0.4)' }} />
                </div>
              )}
              <div style={{
                display: 'grid', gridTemplateColumns: grid, gap: 12, alignItems: 'center',
                padding: '11px 20px',
                borderBottom: i < PREVIEW_STANDINGS.length - 1 ? '1px solid rgba(10,31,68,0.05)' : 'none',
                background: 'white', boxShadow: team.isMe ? 'inset 3px 0 0 #1E5BD6' : 'none',
                opacity: team.rank > PLAYOFF_SPOTS ? 0.6 : 1,
              }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: team.isMe ? V2.blue : V2.faint, fontFamily: JAI_FONTS.mono }}>
                  {String(team.rank).padStart(2, '0')}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: V2.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{team.name}</span>
                  {team.isMe && <BlueTag>You</BlueTag>}
                </div>
                <span style={{ textAlign: 'right', fontSize: 12.5, color: V2.mute, fontFamily: JAI_FONTS.mono }}>{team.projW}-{team.projL}</span>
                <div style={{ height: 7, borderRadius: 999, background: 'rgba(10,31,68,0.06)', overflow: 'hidden' }}>
                  <div style={{ width: `${team.playoffProb}%`, height: '100%', borderRadius: 999, background: team.isMe ? 'linear-gradient(90deg,#1E5BD6,#3A7CFF)' : 'rgba(30,91,214,0.35)' }} />
                </div>
                <span style={{ textAlign: 'right', fontSize: 13, fontWeight: 800, color: team.isMe ? V2.blue : V2.navy, fontFamily: JAI_FONTS.mono }}>{team.playoffProb}%</span>
              </div>
            </Fragment>
          ))}
        </V2Card>

        {/* ── Key insight strip ─────────────────────────────────────────── */}
        <JaiSuggestionStrip style={{ marginBottom: 26 }}>
          {proj.keyInsight}
        </JaiSuggestionStrip>

        {/* ── JAI summary + bull/bear ───────────────────────────────────── */}
        <div style={{ marginBottom: 16 }}>
          <JaiSummaryCard headline="The model's read on your season.">
            {proj.jaiSummary}
          </JaiSummaryCard>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 26 }}>
          <V2Card pad="16px 18px">
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: V2.success, marginBottom: 8 }}>Bull case</div>
            <p style={{ fontSize: 15, color: V2.navy, lineHeight: 1.6, margin: 0 }}>{proj.bullCase}</p>
          </V2Card>
          <V2Card pad="16px 18px">
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: V2.error, marginBottom: 8 }}>Bear case</div>
            <p style={{ fontSize: 15, color: V2.navy, lineHeight: 1.6, margin: 0 }}>{proj.bearCase}</p>
          </V2Card>
        </div>

        {/* ── Per-player ROS outlook ────────────────────────────────────── */}
        <V2SectionHead>Rest-of-season outlook</V2SectionHead>
        <V2Card pad="0">
          {PREVIEW_PLAYERS.map((p, i) => {
            const c = OUTLOOK_COLORS[p.rosOutlook] || V2.faint
            return (
              <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 20px', borderBottom: i < PREVIEW_PLAYERS.length - 1 ? '1px solid rgba(10,31,68,0.05)' : 'none' }}>
                <PlayerPhoto name={p.name} size={30} rounded="full" />
                <div style={{ width: 150, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: V2.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                  <PosChip>{p.pos}</PosChip>
                </div>
                <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase', color: c, fontFamily: JAI_FONTS.mono, width: 46, flexShrink: 0 }}>
                  {p.rosOutlook}
                </span>
                <span style={{ fontSize: 12, color: V2.mute, fontFamily: JAI_FONTS.mono, width: 72, flexShrink: 0 }}>{p.projection}</span>
                <span style={{ fontSize: 12.5, color: 'rgba(10,31,68,0.78)', lineHeight: 1.4, flex: 1 }}>{p.note}</span>
              </div>
            )
          })}
        </V2Card>

      </V2Page>
    </AppShell>
  )
}
